import { axiosBaseQuery } from '@src/service';
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  IActivateRequest,
  IResetPasswordRequest,
  ISignInResponse,
  ITermsResponse,
  IVerifyMfaRequest,
  IVerifyMfaResponse,
  ITotpSetupResponse,
  ITotpVerifyRequest,
  ITotpVerifyResponse,
  ITotpStatusResponse,
  ITotpDisableRequest,
  ITotpDisableResponse,
  IRegenerateBackupCodesRequest,
  IRegenerateBackupCodesResponse,
} from './types';
import { ISignInForm } from '@src/features/auth/pages/sign-in-page/types';
import { IVerifyEmailForm } from '@src/features/auth/pages/verify-email-page/types';


export const authApi = createApi({
  baseQuery: axiosBaseQuery(),
  reducerPath: 'authApi',
  tagTypes: ['TotpStatus'],
  endpoints: (builder) => ({
    activate: builder.mutation<void, IActivateRequest>({
      query: (params) => ({
        url: '/api/v1/activate',
        method: 'GET',
        params,
      })
    }),
    signIn: builder.mutation<ISignInResponse, ISignInForm>({
      query: (data) => ({
        url: '/api/v1/authenticate',
        method: 'POST',
        data,
      })
    }),
    // Second step of the two-step login: exchange the short-lived mfaToken plus a
    // TOTP/backup code for an id_token.
    verifyMfa: builder.mutation<IVerifyMfaResponse, IVerifyMfaRequest>({
      query: (data) => ({
        url: '/api/v1/authenticate/mfa',
        method: 'POST',
        data,
      })
    }),
    verifyEmail: builder.mutation<void, IVerifyEmailForm>({
      query: (data) => ({
        url: '/api/v1/reset-password-request',
        method: 'POST',
        data,
      }),
    }),
    resetPassword: builder.mutation<void, IResetPasswordRequest>({
      query: (data) => ({
        url: '/api/v1/reset-password',
        method: 'POST',
        data,
      })
    }),
    terms: builder.query<ITermsResponse, void>({
      query: () => ({
        url: '/api/v1/terms',
        method: 'GET',
      }),
    }),
    acceptTerms: builder.mutation<void, void>({
      query: () => ({
        url: '/api/v1/terms/accept',
        method: 'POST',
        data: {},
      }),
    }),
    // --- TOTP enrollment / management (auth required, Bearer token added by interceptor) ---
    totpStatus: builder.query<ITotpStatusResponse, void>({
      query: () => ({
        url: '/api/v1/totp/status',
        method: 'GET',
      }),
      providesTags: ['TotpStatus'],
    }),
    totpSetup: builder.mutation<ITotpSetupResponse, void>({
      query: () => ({
        url: '/api/v1/totp/setup',
        method: 'POST',
        data: {},
      }),
    }),
    totpVerify: builder.mutation<ITotpVerifyResponse, ITotpVerifyRequest>({
      query: (data) => ({
        url: '/api/v1/totp/verify',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['TotpStatus'],
    }),
    totpDisable: builder.mutation<ITotpDisableResponse, ITotpDisableRequest>({
      query: (data) => ({
        url: '/api/v1/totp/disable',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['TotpStatus'],
    }),
    regenerateBackupCodes: builder.mutation<IRegenerateBackupCodesResponse, IRegenerateBackupCodesRequest>({
      query: (data) => ({
        url: '/api/v1/totp/backup-codes/regenerate',
        method: 'POST',
        data,
      }),
    }),
  }),
});

export const {
  useSignInMutation,
  useVerifyMfaMutation,
  useActivateMutation,
  useVerifyEmailMutation,
  useResetPasswordMutation,
  useTermsQuery,
  useAcceptTermsMutation,
  useTotpStatusQuery,
  useTotpSetupMutation,
  useTotpVerifyMutation,
  useTotpDisableMutation,
  useRegenerateBackupCodesMutation,
} = authApi;
